import { Link } from "react-router-dom";
import TaskItem from "./TaskItem.js";
import { DateTime, Interval } from "luxon";
import { TileGridProps } from "./sharedTypes.js";
import { packTileView } from "./packTileView.js";
import { TeamScheduleRow } from "./groupTeamSchedule.js";
import React from "react";
import { cn, useIsGummy } from "@swivu/shared-ui";

const TileGrid = ({ teamSchedule, dateRange, timeZone }: TileGridProps) => {
  const packed = packTileView(teamSchedule ?? [], dateRange[0]);
  const isGummy = useIsGummy();
  // return <pre>{JSON.stringify(packed, null, 2)}</pre>;
  return (
    <>
      {packed

        ?.sort((a, b) => a.rowLabel.localeCompare(b.rowLabel))
        // .sort((a, b) => a.row - b.row)
        .map((resource, resourceIndex) => {
          // const resourcePlots = calendarPlot(resource, dateRange, timeZone);
          return (
            <div
              key={`${resource.rowLabel}${resourceIndex}`}
              className=" relative grid-cols-subgrid grid col-span-5 @3xl:col-span-5 gap-1  "
            >
              {isGummy ? (
                <div
                  aria-hidden
                  className="pointer-events-none absolute top-1 -left-2 @3xl:-left-1 h-[calc(100%-0.5rem)] w-[3px] rounded-full bg-gradient-to-b from-mono-300 to-mono-400 dark:from-mono-600 dark:to-mono-700"
                />
              ) : (
                <div
                  className="absolute top-0 -left-2 @3xl:-left-1  h-full bg-mono-400 dark:bg-mono-600 mt-1"
                  style={{ width: "2px" }}
                />
              )}
              {/* <pre>{JSON.stringify(resource, null, 2)}</pre> */}
              <div
                className={cn(
                  "col-span-5 3xl:col-span-1 @3xl:border-t-0 @3xl:pt-0",
                  !isGummy && "dark:border-mono-700/50"
                )}
              >
                <Link
                  to={`${resource?.rowLink}`}
                  className={cn(
                    "block",
                    isGummy
                      ? "ml-1 mr-1 px-2 py-3 rounded-surface-md transition-transform duration-150 gummy-lift active:translate-y-0 active:scale-[0.97] motion-reduce:transform-none hover:bg-mono-200/60 dark:hover:bg-mono-800/60"
                      : "ml-1.5 bg-mono-100 dark:bg-mono-950"
                  )}
                >
                  <p
                    className={cn(
                      "leading-tight",
                      isGummy
                        ? "text-lg font-display font-black tracking-tight text-mono-900 dark:text-white leading-5"
                        : "backdrop-blur-xs leading-4 font-bold"
                    )}
                  >
                    {resource.rowLabel}
                  </p>
                </Link>
              </div>
              <div className="col-span-5 @3xl:col-span-5 @3xl:grid-cols-5 grid grid-cols-5  place-items-stretch *:mx-1 gap-y-1.5">
                <TileGridV2
                  resource={resource}
                  dateRange={dateRange}
                  timeZone={timeZone}
                />
              </div>
            </div>
          );
        })}
    </>
  );
};

const isOnDay = (date: Date, resource: TeamScheduleRow, timeZone: string) => {
  if (!resource.dates || resource.dates.length === 0) return [];

  const checkDateStart = DateTime.fromJSDate(date)
    .setZone(timeZone)
    .startOf("day");
  const checkDateEnd = DateTime.fromJSDate(date).setZone(timeZone).endOf("day");

  const filtered = resource.dates.filter((date) => {
    const startTime = DateTime.fromISO(date.start_time, {
      // zone: timeZone
    });
    const endTime = DateTime.fromISO(date.end_time, {
      // zone: timeZone
    });
    const interval = Interval.fromDateTimes(startTime, endTime);

    // return interval.contains(checkDateStart) || interval.contains(checkDateEnd);
    // console.log(
    //   date.label,
    //   interval.toString(),
    //   Interval.fromDateTimes(checkDateStart, checkDateEnd).toString()
    // );
    return interval.intersection(
      Interval.fromDateTimes(checkDateStart, checkDateEnd)
    );
  });
  return filtered;
};

const TileGridV2 = ({
  resource,
  dateRange,
  timeZone
}: {
  resource: TeamScheduleRow;
  dateRange: Date[];
  timeZone: string;
}) => {
  return (
    <>
      {dateRange.map((date, colIndex) => {
        const eventsOnThisDay = isOnDay(date, resource, timeZone);

        // Sort events for this day: by original row, then by start time for stability
        const sortedEventsOnThisDay = [...eventsOnThisDay].sort((a, b) => {
          if (a.row !== b.row) {
            return a.row - b.row; // Ascending original row order
          }
          const startTimeA = DateTime.fromISO(a.start_time).valueOf();
          const startTimeB = DateTime.fromISO(b.start_time).valueOf();
          return startTimeA - startTimeB; // Ascending start time for ties in original row
        });

        const dailyTileAssignments: {
          event: TeamScheduleRow["dates"][number];
          visualRow: number;
        }[] = [];
        const dailyRowsOccupied = new Set<number>(); // Tracks occupied visual rows for the current day

        for (const event of sortedEventsOnThisDay) {
          let assignedVisualRow = 0;
          while (dailyRowsOccupied.has(assignedVisualRow)) {
            assignedVisualRow++;
          }
          dailyRowsOccupied.add(assignedVisualRow);
          dailyTileAssignments.push({ event, visualRow: assignedVisualRow });
        }

        // Optional: If rendering order needs to strictly follow visualRow, sort dailyTileAssignments here.
        // However, iterating in the order they were processed (based on sortedEventsOnThisDay) might be fine.
        // dailyTileAssignments.sort((a,b) => a.visualRow - b.visualRow);

        return (
          <React.Fragment key={`day-column-${resource.resourceId}-${colIndex}`}>
            {dailyTileAssignments.map(
              ({ event: tileEvent, visualRow }, assignmentIndex) => {
                const startTime = DateTime.fromISO(tileEvent.start_time, {
                  zone: timeZone
                });
                const endTime = DateTime.fromISO(tileEvent.end_time, {
                  zone: timeZone
                });
                const plotDate = DateTime.fromJSDate(date, {
                  zone: timeZone
                });

                const startsAfterMidnight =
                  startTime.hasSame(plotDate, "day") && startTime.hour !== 0;
                const endsBeforeMidnight =
                  endTime.hasSame(plotDate, "day") && endTime.hour !== 0;

                return (
                  <TaskItem
                    // Using a combination for unique keys across days and items within a day
                    viewTransitionUnique={`${tileEvent.resourceId}-${tileEvent.label}-${colIndex}-${visualRow}`}
                    key={`${tileEvent.resourceId}-${tileEvent.label}-${colIndex}-${assignmentIndex}`}
                    taskId={tileEvent.resourceId} // This is the event's original resourceId
                    color={tileEvent?.color}
                    style={tileEvent.style}
                    row={`${visualRow + 1}`} // Use the new daily visualRow (1-indexed for CSS)
                    gridColumn={`${colIndex + 1}`} // 1-indexed for CSS
                    // taskDisplayName={`${colIndex + 1} - ${visualRow}`} // Debug: display 1-indexed col, 0-indexed visualRow
                    taskDisplayName={`${tileEvent.label}`} // Debug: display 1-indexed col, 0-indexed visualRow
                    link={tileEvent.dateLink}
                    startsAt={
                      startsAfterMidnight
                        ? startTime.toFormat("h:mm a")
                        : undefined
                    }
                    endsAt={
                      endsBeforeMidnight
                        ? endTime.toFormat("h:mm a")
                        : undefined
                    }
                  />
                );
              }
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default TileGrid;
